import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Close, People } from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';
import { useDistributors } from '../../hooks';

const initialFormData = {
  nombre: '',
  celular: '',
  email_ewelink: '',
  ubicacion_texto: '',
  ubicacion_url: '',
  distributor_id: '',
};

const ContactFormModalComponent = ({ open, onClose, onSubmit, entity, isEditing }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  
  // Hook para obtener distribuidores activos
  const { activeDistributors, activeDistributorsLoading, loadActiveDistributors } = useDistributors();

  useEffect(() => {
    if (open) {
      // Cargar distribuidores activos cuando se abre el modal
      loadActiveDistributors();
      
      if (isEditing && entity) {
        setFormData({
          nombre: entity.nombre || '',
          celular: entity.celular || '',
          email_ewelink: entity.email_ewelink || '',
          ubicacion_texto: entity.ubicacion_texto || '',
          ubicacion_url: entity.ubicacion_url || '',
          distributor_id: entity.distributor_id || '',
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
      setSubmitError('');
    }
  }, [entity, isEditing, open, loadActiveDistributors]);

  const handleInputChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido.';
    if (!formData.celular.trim()) {
      newErrors.celular = 'El número de celular es requerido.';
    } else {
      const cleanCelular = formData.celular.replace(/[\s\-\(\)\+]/g, '');
      // Permitir 10 dígitos (México sin prefijo) o números con prefijo internacional
      if (!/^(\d{10}|\d{1,4}\d{10,12})$/.test(cleanCelular)) {
        newErrors.celular = 'Debe tener 10 dígitos (ej: 2299288981) o incluir prefijo internacional (ej: +1 1234567890)';
      }
    }
    // Opcional: Validar formato de email de eWeLink si se desea ser estricto
    if (formData.email_ewelink && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_ewelink)) {
      newErrors.email_ewelink = 'El formato del correo de eWeLink no es válido.';
    }
    // Opcional: Validar formato de URL
    if (formData.ubicacion_url && !/^https?:\/\/.+/.test(formData.ubicacion_url)) {
      newErrors.ubicacion_url = 'Por favor, introduce una URL válida (ej. http://...).';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!validate()) return;
    
    try {
      await onSubmit(formData);
    } catch (error) {
      setSubmitError(error.message || 'Ocurrió un error al guardar el contacto.');
    }
  }, [formData, onSubmit, validate]);
  
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle
        sx={{
          padding: 4,
          paddingBottom: 3,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <People sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '1.25rem' }}>
                {isEditing ? 'Editar Contacto' : 'Nuevo Contacto'}
              </Typography>
              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.875rem' }}>
                {isEditing ? 'Modifica los datos del contacto.' : 'Agrega un nuevo contacto al sistema.'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: THEME_COLORS.TEXT_SECONDARY, '&:hover': { backgroundColor: THEME_COLORS.BACKGROUND_PAPER, color: THEME_COLORS.TEXT_PRIMARY } }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {submitError}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, fontSize: '1rem', marginBottom: 2 }}>
              Información Principal
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nombre Completo"
              placeholder="Ej: Juan Pérez"
              value={formData.nombre}
              onChange={handleInputChange('nombre')}
              error={!!errors.nombre}
              helperText={errors.nombre || 'El nombre para identificar al contacto.'}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Número de Celular"
              placeholder="Ej: 2299288981 o +1 1234567890"
              value={formData.celular}
              onChange={handleInputChange('celular')}
              error={!!errors.celular}
              helperText={errors.celular || 'Escribe 10 dígitos (se agrega +52 automáticamente) o incluye otro prefijo.'}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl 
              fullWidth 
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            >
              <InputLabel>Distribuidor</InputLabel>
              <Select
                value={formData.distributor_id}
                onChange={handleInputChange('distributor_id')}
                label="Distribuidor"
                disabled={activeDistributorsLoading}
                endAdornment={activeDistributorsLoading && <CircularProgress size={20} sx={{ mr: 2 }} />}
              >
                <MenuItem value="">
                  <em>Sin distribuidor asignado</em>
                </MenuItem>
                {activeDistributors.map((distributor) => (
                  <MenuItem key={distributor.distributor_id} value={distributor.distributor_id}>
                    {distributor.nombre_empresa} - {distributor.nombre_distribuidor}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_MUTED, mt: 0.5, ml: 1.5 }}>
                Selecciona el distribuidor responsable de este contacto (opcional).
              </Typography>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, fontSize: '1rem', marginBottom: 2, mt: 2 }}>
              Información Adicional (Opcional)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email de eWeLink"
              placeholder="Ej: juan.perez@ewelink.com"
              value={formData.email_ewelink}
              onChange={handleInputChange('email_ewelink')}
              error={!!errors.email_ewelink}
              helperText={errors.email_ewelink || 'Correo asociado a la cuenta de eWeLink.'}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Ubicación (Texto)"
              placeholder="Ej: Oficina Principal, Sala de Reuniones"
              value={formData.ubicacion_texto}
              onChange={handleInputChange('ubicacion_texto')}
              error={!!errors.ubicacion_texto}
              helperText={errors.ubicacion_texto || 'Una descripción de la ubicación.'}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Ubicación (URL de Google Maps)"
              placeholder="Ej: https://maps.app.goo.gl/..."
              value={formData.ubicacion_url}
              onChange={handleInputChange('ubicacion_url')}
              error={!!errors.ubicacion_url}
              helperText={errors.ubicacion_url || 'El enlace a la ubicación en Google Maps.'}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          p: { xs: 2, sm: 3 },
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 120 },
            color: THEME_COLORS.TEXT_SECONDARY,
            borderColor: THEME_COLORS.BORDER_LIGHT,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 140 },
            backgroundColor: THEME_COLORS.TEXT_PRIMARY,
            color: THEME_COLORS.BACKGROUND_PAPER,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#1a1a1a',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {isEditing ? 'Actualizar Contacto' : 'Crear Contacto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ContactFormModalComponent.displayName = 'ContactFormModal';
const ContactFormModal = memo(ContactFormModalComponent);

export default ContactFormModal; 